import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { useCreateEstimateNorm } from '../shared/hooks/useEstimateNormsApply';
import { useResourceRates } from '../rates/hooks/useResourceRates';
import type { EstimateNormResourceInput } from '../shared/types/estimateNorms.types';
import { toast } from 'sonner';

export function NormNewPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const createNorm = useCreateEstimateNorm();

  // Load resources for the dropdowns
  const { resources: materials } = useResourceRates('MATERIAL');
  const { resources: labors } = useResourceRates('LABOR');
  const { resources: equipments } = useResourceRates('EQUIPMENT');
  const { resources: agros } = useResourceRates('AGRO');

  const [form, setForm] = useState({
    norm_code: '',
    norm_name: '',
    category_code: 'trees',
    unit: 'cây',
    norm_type: 'labor',
    apply_to: 'all',
    status_code: 'active',
    description: '',
    notes: '',
  });

  const [images, setImages] = useState<File[]>([]);
  const [resources, setResources] = useState<EstimateNormResourceInput[]>([]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter(f => (f.type === 'image/jpeg' || f.type === 'image/png') && f.size <= 5 * 1024 * 1024);
      
      if (validFiles.length !== droppedFiles.length) {
        toast.error('Chỉ hỗ trợ file JPG/PNG và dung lượng < 5MB');
      }

      setImages(prev => {
        const newImages = [...prev, ...validFiles];
        if (newImages.length > 5) {
          toast.error('Chỉ được tải lên tối đa 5 ảnh');
          return newImages.slice(0, 5);
        }
        return newImages;
      });
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile?.company_id) return;
    
    // Validate resources
    if (resources.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 định mức hao phí tài nguyên');
      return;
    }
    
    const invalidResource = resources.find(r => !r.resource_name || r.norm_quantity === undefined || r.norm_quantity <= 0);
    if (invalidResource) {
      toast.error('Vui lòng điền đủ thông tin tài nguyên (tên, định mức > 0)');
      return;
    }

    // Atomic upload mock (would be implemented here before createNorm)
    const uploadedImageUrls: string[] = []; // Mock
    
    try {
      const id = await createNorm.mutateAsync({
        input: {
          company_id: profile.company_id,
          norm_code: form.norm_code.trim() || undefined, // backend auto-generates if empty
          norm_name: form.norm_name.trim(),
          category_code: form.category_code,
          unit: form.unit.trim(),
          norm_type: form.norm_type,
          apply_to: form.apply_to,
          status_code: form.status_code,
          description: form.description.trim() || null,
          notes: form.notes.trim() || null,
          images: uploadedImageUrls.length > 0 ? uploadedImageUrls : null,
        },
        resources: resources,
      });
      navigate(`/estimates/norms/${id}`);
    } catch (error) {
      // Error is handled by hook
    }
  };

  const addResource = () => {
    if (!profile?.company_id) return;
    setResources([...resources, { company_id: profile.company_id, resource_type: 'material', resource_name: '', unit: '', norm_quantity: 1, waste_percent: 0 }]);
  };

  const updateResource = (index: number, patch: Partial<EstimateNormResourceInput>) => {
    setResources(resources.map((resource, idx) => (idx === index ? { ...resource, ...patch } : resource)));
  };

  const getResourceListOptions = () => {
    switch(form.norm_type) {
      case 'material': return materials.map(m => ({ ...m, type: 'material' }));
      case 'labor': return labors.map(m => ({ ...m, type: 'labor' }));
      case 'equipment': return equipments.map(m => ({ ...m, type: 'equipment' }));
      case 'agro': return agros.map(m => ({ ...m, type: 'agro' }));
      case 'other': return [
        ...materials.map(m => ({ ...m, type: 'material' })),
        ...labors.map(m => ({ ...m, type: 'labor' })),
        ...equipments.map(m => ({ ...m, type: 'equipment' })),
        ...agros.map(m => ({ ...m, type: 'agro' }))
      ];
      default: return [];
    }
  };

  const handleResourceSelect = (index: number, resourceName: string) => {
    const optionsList = getResourceListOptions();
    const selected = optionsList.find(r => r.name === resourceName);
    
    if (selected) {
      updateResource(index, { 
        resource_id: selected.id, 
        resource_code: selected.code,
        resource_name: selected.name, 
        unit: selected.unit,
        resource_type: selected.type
      });
    } else {
      updateResource(index, { resource_name: resourceName });
    }
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="shrink-0 p-4">
        <PageHeaderNew 
          title="Thêm định mức" 
          subtitle="Khai báo định mức hao phí nhân công, vật tư, thiết bị cho một công việc cụ thể" 
          backUrl="/estimates/norms" 
        />
      </div>

      <form id="normForm" onSubmit={submit} className="min-h-0 flex-1 overflow-auto px-4 pb-24 pt-2">
        <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
          <div className="space-y-4">
            {/* THÔNG TIN ĐỊNH MỨC */}
            <Card className="border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-emerald-800">Thông tin định mức</h2>
          
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Mã định mức</label>
              <input value={form.norm_code} onChange={(e) => setForm({ ...form, norm_code: e.target.value })} placeholder="Tự sinh nếu bỏ trống" className="field w-full" />
            </div>
            
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Tên công việc <span className="text-rose-500">*</span></label>
              <input required value={form.norm_name} onChange={(e) => setForm({ ...form, norm_name: e.target.value })} placeholder="VD: Cắt tỉa cây bụi thấp" className="field w-full" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Nhóm công việc <span className="text-rose-500">*</span></label>
              <select required value={form.category_code} onChange={(e) => setForm({ ...form, category_code: e.target.value })} className="field w-full">
                <option value="trees">Cây bóng mát</option>
                <option value="shrubs">Cây bụi / hàng rào</option>
                <option value="grass">Cỏ / phủ nền</option>
                <option value="irrigation">Hệ thống tưới</option>
                <option value="lighting">Đèn cảnh quan</option>
                <option value="hardscape">Hardscape</option>
                <option value="other">Khác</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Đơn vị tính <span className="text-rose-500">*</span></label>
              <input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="VD: cây, m2, m dài..." className="field w-full" />
            </div>



            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Áp dụng cho</label>
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="all" checked={form.apply_to === 'all'} onChange={(e) => setForm({ ...form, apply_to: e.target.value })} className="text-emerald-600 focus:ring-emerald-600" />
                  <span className="text-sm text-slate-700">Tất cả công trình</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="specific" checked={form.apply_to === 'specific'} onChange={(e) => setForm({ ...form, apply_to: e.target.value })} className="text-emerald-600 focus:ring-emerald-600" />
                  <span className="text-sm text-slate-700">Chọn công trình</span>
                </label>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Trạng thái</label>
              <select value={form.status_code} onChange={(e) => setForm({ ...form, status_code: e.target.value })} className="field w-full">
                <option value="active">Đang áp dụng</option>
                <option value="draft">Bản nháp</option>
                <option value="inactive">Ngừng áp dụng</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Mô tả công việc</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Mô tả chi tiết công việc..." className="field min-h-24 w-full py-2" />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Ghi chú</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ghi chú thêm..." className="field min-h-24 w-full py-2" />
            </div>
          </div>
        </Card>

        {/* HÌNH ẢNH MINH HỌA */}
        <Card className="border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-1 text-sm font-medium tracking-wide text-emerald-800">Hình ảnh minh họa <span className="font-normal text-slate-500">(tùy chọn)</span></h2>
          <p className="mb-4 text-xs text-slate-500">Thêm hình ảnh để mô tả rõ hơn công việc (Tối đa 5 ảnh)</p>
          
          <div className="flex flex-wrap gap-4">
            <div 
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="flex h-32 w-48 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-slate-50 transition-colors hover:border-emerald-500 hover:bg-emerald-50"
              onClick={() => document.getElementById('image-upload')?.click()}
            >
              <i className="ri-upload-cloud-2-line mb-2 text-2xl text-slate-400"></i>
              <span className="text-center text-xs text-slate-500 px-2">Kéo thả hoặc click để chọn</span>
              <input 
                id="image-upload" 
                type="file" 
                multiple 
                accept="image/jpeg, image/png" 
                className="hidden" 
                onChange={(e) => {
                  if (e.target.files) {
                    const droppedFiles = Array.from(e.target.files);
                    const validFiles = droppedFiles.filter(f => (f.type === 'image/jpeg' || f.type === 'image/png') && f.size <= 5 * 1024 * 1024);
                    setImages(prev => [...prev, ...validFiles].slice(0, 5));
                  }
                }} 
              />
            </div>
            
            {images.map((img, idx) => (
              <div key={idx} className="group relative h-32 w-48 overflow-hidden rounded-md border border-slate-200">
                <img src={URL.createObjectURL(img)} alt="preview" className="h-full w-full object-cover" />
                <button 
                  type="button"
                  onClick={() => setImages(images.filter((_, i) => i !== idx))}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/80 text-rose-600 opacity-0 shadow backdrop-blur transition-opacity group-hover:opacity-100"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}
          </div>
        </Card>

          </div>

          <div className="flex flex-col max-h-[calc(100vh-8rem)] xl:sticky xl:top-2">
            {/* TÀI NGUYÊN ĐỊNH MỨC */}
            <Card className="flex min-h-0 flex-col border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex shrink-0 items-center justify-between">
            <div>
              <h2 className="text-sm font-medium tracking-wide text-emerald-800">Định mức hao phí tài nguyên</h2>
              <p className="text-xs text-slate-500 mt-0.5">Liệt kê khối lượng tiêu hao về nhân công, vật tư, thiết bị cho 1 đơn vị tính</p>
            </div>
            <Button type="button" variant="secondary" onClick={addResource}>
              <i className="ri-add-line mr-2" />Thêm nguồn lực
            </Button>
          </div>

          <div className="mb-6 shrink-0">
            <label className="mb-2 block text-xs font-medium text-slate-700">Nguồn lực</label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { id: 'labor', name: 'Nhân công', icon: 'ri-user-settings-line' },
                { id: 'material', name: 'Vật tư', icon: 'ri-apps-2-line' },
                { id: 'equipment', name: 'Thiết bị', icon: 'ri-truck-line' },
                { id: 'other', name: 'Khác', icon: 'ri-stack-line' }
              ].map(type => (
                <label key={type.id} className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${form.norm_type === type.id ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="norm_type" value={type.id} checked={form.norm_type === type.id} onChange={(e) => setForm({ ...form, norm_type: e.target.value })} className="h-4 w-4 text-emerald-600 focus:ring-emerald-600" />
                  <div className="flex flex-col">
                    <span className={`text-sm ${form.norm_type === type.id ? 'font-medium text-emerald-800' : 'text-slate-700'}`}>{type.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {resources.length === 0 ? (
            <div className="flex shrink-0 flex-col items-center justify-center rounded-md border border-dashed border-slate-300 py-12">
              <i className="ri-database-2-line mb-3 text-3xl text-slate-300"></i>
              <p className="text-sm font-medium text-slate-600">Chưa có nguồn lực nào</p>
              <p className="mb-4 mt-1 text-xs text-slate-500">Thêm nhân công, vật tư hoặc thiết bị cấu thành</p>
              <Button type="button" variant="outline" onClick={addResource}>
                Thêm nguồn lực đầu tiên
              </Button>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto pr-1 overflow-x-hidden">
              <div className="w-full space-y-2">
                <div className="sticky top-0 z-10 grid grid-cols-[1fr_60px_70px_80px_32px] gap-2 bg-white px-2 py-2 text-xs font-medium text-slate-500 shadow-sm border-b border-slate-100">
                  <div className="truncate">Tên nguồn lực</div>
                  <div className="truncate text-center">Đơn vị</div>
                  <div className="truncate text-center">Hệ số (%)</div>
                  <div className="truncate text-center">Định mức</div>
                  <div></div>
                </div>
                
                {resources.map((resource, index) => {
                  const optionsList = getResourceListOptions();
                  
                  return (
                    <div key={index} className="grid items-center grid-cols-[1fr_60px_70px_80px_32px] gap-2 rounded-md border border-slate-100 bg-slate-50 p-2 transition-colors hover:border-emerald-100 hover:bg-emerald-50/50">
                      
                      <div className="relative">
                        <input 
                          list={`resources-list-${index}`}
                          value={resource.resource_name} 
                          onChange={(e) => handleResourceSelect(index, e.target.value)} 
                          placeholder="Chọn hoặc nhập mới..." 
                          className="field w-full bg-white text-sm" 
                        />
                        <datalist id={`resources-list-${index}`}>
                          {optionsList?.map(opt => (
                            <option key={opt.id} value={opt.name} />
                          ))}
                        </datalist>
                      </div>

                      <input 
                        value={resource.unit} 
                        onChange={(e) => updateResource(index, { unit: e.target.value })} 
                        placeholder="ĐVT" 
                        className="field w-full bg-white text-sm text-center px-1" 
                      />
                      
                      <input 
                        type="number" 
                        step="0.01"
                        min="0"
                        value={resource.waste_percent} 
                        onChange={(e) => updateResource(index, { waste_percent: Number(e.target.value) })} 
                        className="field w-full bg-white text-sm text-center px-1" 
                      />

                      <input 
                        type="number" 
                        step="0.0001"
                        min="0"
                        required
                        value={resource.norm_quantity} 
                        onChange={(e) => updateResource(index, { norm_quantity: Number(e.target.value) })} 
                        className="field w-full bg-white text-sm text-right px-2" 
                      />
                      
                      <button 
                        type="button" 
                        onClick={() => setResources(resources.filter((_, idx) => idx !== index))}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-rose-100 hover:text-rose-600"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
            </Card>
          </div>
        </div>
      </form>

      {/* STICKY FOOTER */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <p className="text-xs text-slate-500">Bạn đang tạo phiên bản định mức mới. Mọi thông tin sẽ được lưu lại lịch sử.</p>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={() => navigate('/estimates/norms')}>Hủy bỏ</Button>
            <AuthButton 
              type="button"
              behavior="disable"
              permission="estimates.norms.create"
              isLoading={createNorm.isPending} 
              onClick={() => (document.getElementById('normForm') as HTMLFormElement)?.requestSubmit()}
            >
              Lưu định mức
            </AuthButton>
          </div>
        </div>
      </div>
    </div>
  );
}
